import { assertEuro, materialNetUnitCost, normalizeLineDiscount } from './quoteFinancialInput';
/**
 * Contrat frontend du moteur quote_engine V1.
 *
 * Ce module est volontairement le seul endroit où l'état d'édition React est
 * traduit vers le contrat JSON Python. Aucune formule de prix de vente n'est
 * calculée ici : le moteur reste l'unique source de vérité économique.
 */

import { engineRoundingMode as commercialRoundingMode } from './quoteRounding';

export const QUOTE_ENGINE_SCHEMA_VERSION = 1;

export const QUOTE_ENGINE_ROUTES = Object.freeze({
  calculate: '/api/v1/quotes/calculate',
  validate: '/api/v1/quotes/validate',
  snapshots: '/api/v1/quotes/snapshots',
});

const ENGINE_UNITS = new Set(['Ens', 'U', 'm', 'ml']);

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Decimal est sérialisé en chaîne dans le contrat V1 afin de ne jamais faire
 * dépendre le moteur des arrondis binaires de Number côté JavaScript.
 */
export const asDecimalString = (value, fallback = '0') => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.').trim();
    return normalized !== '' && Number.isFinite(Number(normalized)) ? normalized : fallback;
  }
  if (!Number.isFinite(Number(value))) return fallback;
  if (typeof value === 'number') {
    return value.toFixed(12).replace(/\.?0+$/, '') || '0';
  }
  return String(value);
};

const ratioString = (percentage) => {
  const value = asNumber(percentage, 0) / 100;
  return value.toFixed(12).replace(/\.?0+$/, '') || '0';
};

export const normalizeEngineUnit = (unit) => {
  if (ENGINE_UNITS.has(unit)) return unit;
  const normalized = String(unit || '').trim().toLowerCase();
  if (normalized === 'u' || normalized === 'unité' || normalized === 'unite') return 'U';
  if (normalized === 'm') return 'm';
  if (normalized === 'ml' || normalized === 'mètre linéaire' || normalized === 'metre lineaire') return 'ml';
  return 'Ens';
};

const parameterValue = (parameters, code, fallback) => {
  const item = (parameters || []).find((parameter) => parameter.parameterCode === code);
  return item?.valueNumber ?? item?.valueText ?? fallback;
};

const lineQuantity = (line) => Math.max(0, asNumber(line.quantity, 1));

const laborComponents = (line, editor) => {
  const quantity = lineQuantity(line);
  const items = Array.isArray(line.laborItems) ? line.laborItems : [];
  if (items.length) {
    return items.map((item, index) => {
      const totalHours = Math.max(0, asNumber(item.resourceQuantity, 1))
        * Math.max(0, asNumber(item.unitHours, 0));
      const unitHours = quantity > 0 ? totalHours / quantity : 0;
      return {
        nature: 'labor',
        unit_cost: '0',
        labor_profile: {
          code: String(item.profileCodeSnapshot || item.profileCode || `labor-${index + 1}`),
          label: String(item.profileLabelSnapshot || item.profileLabel || 'Main-d’œuvre'),
          hourly_cost: asDecimalString(
            item.rateMode === 'manual'
              ? (item.costHourlyRateSnapshot ?? item.costHourlyRate ?? editor.defaultLaborRate ?? 0)
              : parameterValue(
                editor.versionParameters,
                `laborRate.${item.profileCodeSnapshot || item.profileCode || line.laborProfileCode || ''}`,
                item.costHourlyRateSnapshot ?? item.costHourlyRate ?? line.laborRate ?? editor.defaultLaborRate ?? 0
              )
          ),
        },
        unit_time_hours: asDecimalString(unitHours),
        fixed_cost: '0',
      };
    });
  }

  const hours = Math.max(0, asNumber(line.estimatedHours ?? line.estimated_hours, 0));
  if (!hours) return [];
  return [{
    nature: 'labor',
    unit_cost: '0',
    labor_profile: {
      code: String(line.laborProfileCode || 'default-labor'),
      label: String(line.laborProfileLabel || 'Main-d’œuvre'),
      hourly_cost: asDecimalString(
        parameterValue(
          editor.versionParameters,
          `laborRate.${line.laborProfileCode || ''}`,
          line.laborRate ?? editor.defaultLaborRate ?? 0
        )
      ),
    },
    unit_time_hours: asDecimalString(hours),
    fixed_cost: '0',
  }];
};

const materialComponents = (line) => {
  const quantity = lineQuantity(line);
  const components = [];
  const items = Array.isArray(line.materials) ? line.materials : [];
  items.forEach((item) => {
    const itemQuantity = Math.max(0, asNumber(item.quantity, 1));
    const total = materialNetUnitCost(item) * itemQuantity;
    if (total) components.push({
      nature: 'material',
      unit_cost: quantity > 0 ? asDecimalString(total / quantity) : '0',
      labor_profile: null,
      unit_time_hours: '0',
      fixed_cost: quantity > 0 ? '0' : asDecimalString(total),
    });
  });

  const manualUnitCost = Math.max(0, asNumber(line.purchaseCost ?? line.purchase_cost ?? line.unitPrice, 0));
  if (manualUnitCost) components.push({
    nature: 'material',
    unit_cost: asDecimalString(manualUnitCost),
    labor_profile: null,
    unit_time_hours: '0',
    fixed_cost: '0',
  });
  return components;
};

const subcontractingComponents = (line) => {
  const quantity = lineQuantity(line);
  const components = [];
  const items = Array.isArray(line.subcontractingItems) ? line.subcontractingItems : [];
  items.forEach((item) => {
    const unit = Math.max(0, asNumber(
      item.purchasePriceMode === 'forced'
        ? item.forcedUnitPurchasePrice
        : item.unitPurchasePrice,
      0
    ));
    const itemQuantity = Math.max(0, asNumber(item.quantity, 1));
    const total = unit * itemQuantity;
    if (total) components.push({
      nature: 'subcontracting',
      unit_cost: quantity > 0 ? asDecimalString(total / quantity) : '0',
      labor_profile: null,
      unit_time_hours: '0',
      fixed_cost: quantity > 0 ? '0' : asDecimalString(total),
    });
  });

  const manualUnitCost = Math.max(0, asNumber(line.subcontractingCost ?? line.subcontracting_cost, 0));
  if (manualUnitCost) components.push({
    nature: 'subcontracting',
    unit_cost: asDecimalString(manualUnitCost),
    labor_profile: null,
    unit_time_hours: '0',
    fixed_cost: '0',
  });
  return components;
};

const otherDirectCostComponents = (line) => {
  const amount = Math.max(0, asNumber(line.otherDirectCost ?? line.other_direct_cost, 0));
  if (!amount) return [];
  const quantity = lineQuantity(line);
  return [{
    nature: 'other',
    unit_cost: quantity > 0 ? asDecimalString(amount / quantity) : '0',
    labor_profile: null,
    unit_time_hours: '0',
    fixed_cost: quantity > 0 ? '0' : asDecimalString(amount),
  }];
};

const lineCoefficient = (line, editor) => asDecimalString(
  line.forcedCoefficient
    ?? line.manualCoefficient
    ?? line.effectiveCoefficient
    ?? line.marginCoefficient
    ?? editor.defaultMarginCoefficient
    ?? 1
);

const activeNatures = (components) => [...new Set(components.map((component) => component.nature))];

const linePricing = (line, editor, components) => {
  const coefficient = lineCoefficient(line, editor);
  const hasLineOverride = ['manual', 'forced', 'calculated'].includes(line.coefficientMode)
    || (line.manualCoefficient !== null && line.manualCoefficient !== undefined)
    || (line.forcedCoefficient !== null && line.forcedCoefficient !== undefined);
  const automaticPricing = {
    mode: 'cost_plus_nature_coefficients', coefficient: '1', margin_rate: '0',
    markup_rate: '0', fixed_unit_selling_price: null,
  };
  const overrides = hasLineOverride
    ? activeNatures(components).map((nature) => ({ nature, coefficient })) : [];
  if (line.sellingPriceMode === 'forced' && line.forcedUnitSellingPrice !== null
      && line.forcedUnitSellingPrice !== undefined) {
    return {
      pricing: {
        mode: 'fixed_selling_price',
        coefficient: '1',
        margin_rate: '0',
        markup_rate: '0',
        fixed_unit_selling_price: asDecimalString(line.forcedUnitSellingPrice),
      },
      automaticPricing,
      overrides,
    };
  }

  return {
    pricing: automaticPricing,
    overrides,
  };
};

const lineDiscount = (line) => {
  const { discountKind: kind, discountValue: value } = normalizeLineDiscount(line);
  if (value === null) return null;
  return { kind, value: kind === "percent" ? ratioString(value) : asDecimalString(value) };
};

const manualAllocationForLine = (editor, line) => {
  const manualExpense = (editor.versionExpenses || []).find((expense) => expense.enabled !== false && expense.allocationMode === 'manual');
  if (!manualExpense) return line.manualAllocatedExpenseAmount ?? 0;
  const allocation = (manualExpense.allocations || []).find(
    (item) => String(item.quoteLineId) === String(line.id)
  );
  return allocation?.manualAmount ?? allocation?.effectiveAmount ?? 0;
};

const toEngineLine = (line, editor) => {
  const components = [
    ...laborComponents(line, editor),
    ...materialComponents(line),
    ...subcontractingComponents(line),
    ...otherDirectCostComponents(line),
  ];
  const pricing = linePricing(line, editor, components);
  return {
    id: String(line.id ?? line.tempId ?? ''),
    label: String(line.description || line.label || 'Ligne sans désignation'),
    quantity: asDecimalString(line.quantity ?? 1, '1'),
    unit: normalizeEngineUnit(line.unit),
    cost_components: components,
    pricing: pricing.pricing,
    ...(pricing.automaticPricing ? { automatic_pricing: pricing.automaticPricing } : {}),
    nature_coefficient_overrides: pricing.overrides,
    discount: lineDiscount(line),
    tax: { rate: ratioString(line.vatRate ?? editor.defaultVatRate ?? 0) },
    allocation_weight: asDecimalString(line.weightingCoefficient ?? 1, '1'),
    manual_overhead_allocation: asDecimalString(manualAllocationForLine(editor, line)),
  };
};

/** Reconstruit la hiérarchie de chapitres attendue par quote_engine. */
export const buildEngineChapters = (sections = [], editor = {}) => {
  const nodes = new Map((sections || []).map((section, index) => [String(section.id), {
    id: String(section.id ?? `section-${index}`),
    title: String(section.title || 'Chapitre'),
    lines: (section.lines || []).filter((line) => !['text', 'subtotal'].includes(line.lineType)).map((line) => toEngineLine(line, editor)),
    chapters: [],
    _parent: section.parentSectionId ?? null,
    _order: Number(section.orderIndex ?? index),
  }]));

  const roots = [];
  [...nodes.values()].sort((a, b) => a._order - b._order).forEach((node) => {
    const parent = node._parent === null || node._parent === undefined
      ? null
      : nodes.get(String(node._parent));
    if (parent) parent.chapters.push(node);
    else roots.push(node);
  });

  const clean = (node) => ({
    id: node.id,
    title: node.title,
    lines: node.lines,
    chapters: node.chapters.sort((a, b) => a._order - b._order).map(clean),
  });
  return roots.map(clean);
};

const expenseAmount = (expense) => asDecimalString(
  expense.amountMode === 'forced'
    ? expense.forcedAmount
    : expense.inputValue ?? expense.effectiveAmount ?? expense.calculatedAmount ?? 0
);

const allocationMethod = (mode) => ({
  amount: 'by_amount',
  cost: 'by_amount',
  hours: 'by_labor_hours',
  labor_cost: 'by_labor_cost',
  material_cost: 'by_material_cost',
  quantity: 'by_quantity',
  weighting: 'by_weight',
  manual: 'manual',
  fixed: 'fixed',
}[mode] || 'by_amount');

const expenseGroups = (editor) => {
  const directExpenses = [];
  const overheads = [];
  const imputedChargeRules = [];
  const sellingPriceExpenses = [];

  (editor.versionExpenses || []).forEach((expense) => {
    const label = String(expense.labelSnapshot || expense.label || 'Frais');
    const category = expense.categorySnapshot || expense.category_snapshot || '';
    const type = expense.calculationType || expense.calculation_type || 'fixed_amount';
    const base = expense.calculationBase || expense.calculation_base || 'direct_cost';
    const allocationMode = expense.allocationMode || expense.allocation_mode || (category === 'direct_expense' ? 'none' : 'cost');

    if (type === 'percentage' && base === 'selling_price') {
      sellingPriceExpenses.push({
        label,
        rate: ratioString(expense.inputValue ?? 0),
        enabled: expense.enabled !== false,
      });
      return;
    }

    if (expense.enabled === false) return;

    if (type === 'percentage') {
      const isLaborHours = base === 'labor_hours';
      const rate = isLaborHours ? asDecimalString(expense.inputValue ?? 0) : ratioString(expense.inputValue ?? 0);
      const natures = isLaborHours || base === 'labor_cost'
        ? ['labor']
        : base === 'material_cost'
          ? ['material']
          : base === 'subcontracting_cost'
            ? ['subcontracting']
            : base === 'co_contracting_cost'
              ? ['co_contracting']
              : base === 'other_cost'
                ? ['other']
                : ['labor', 'material', 'subcontracting', 'co_contracting', 'internal_study', 'external_study', 'other'];
      natures.forEach((nature) => imputedChargeRules.push({
        nature,
        basis: isLaborHours ? 'labor_hours' : 'cost_amount',
        rate,
      }));
      return;
    }

    const amount = expenseAmount(expense);
    if (category === 'direct_expense' || allocationMode === 'none') {
      directExpenses.push({ label, amount });
      return;
    }

    overheads.push({
      label,
      amount,
      allocation_method: allocationMethod(allocationMode),
      fixed_line_id: expense.fixedLineId ? String(expense.fixedLineId) : null,
    });
  });

  return { directExpenses, overheads, imputedChargeRules, sellingPriceExpenses };
};

const natureCoefficients = (editor) => {
  const parameters = editor.versionParameters || [];
  return [
    ['labor', parameterValue(parameters, 'laborCoefficient', editor.defaultMarginCoefficient ?? 1)],
    ['material', parameterValue(parameters, 'materialCoefficient', editor.defaultMarginCoefficient ?? 1)],
    ['subcontracting', parameterValue(parameters, 'subcontractingCoefficient', editor.defaultMarginCoefficient ?? 1)],
    ['co_contracting', parameterValue(parameters, 'coContractingCoefficient', editor.defaultMarginCoefficient ?? 1)],
    ['internal_study', parameterValue(parameters, 'internalStudyCoefficient', parameterValue(parameters, 'laborCoefficient', editor.defaultMarginCoefficient ?? 1))],
    ['external_study', parameterValue(parameters, 'externalStudyCoefficient', editor.defaultMarginCoefficient ?? 1)],
    ['other', parameterValue(parameters, 'otherCoefficient', editor.defaultMarginCoefficient ?? 1)],
  ].map(([nature, coefficient]) => ({ nature, coefficient: asDecimalString(coefficient, '1') }));
};

const globalDiscount = (editor) => {
  const value = editor.globalDiscountValue ?? editor.global_discount_value;
  if (!value || asNumber(value) === 0) return null;
  const kind = editor.globalDiscountKind || editor.global_discount_kind || 'percent';
  return { kind, value: kind === 'percent' ? ratioString(value) : asDecimalString(value) };
};

const financialTerms = (editor) => ({
  payment_terms: editor.paymentTerms || null,
  deposit: asNumber(editor.depositRate) > 0
    ? { kind: 'percent', value: ratioString(editor.depositRate) }
    : null,
  validity_days: editor.validityDays === '' ? 30 : Number(editor.validityDays ?? 30),
});

const priceAdjustment = (editor) => {
  const revisionFormula = parameterValue(editor.versionParameters, 'revisionFormula', '');
  const escalationFormula = parameterValue(editor.versionParameters, 'escalationFormula', '');
  const formula = revisionFormula || escalationFormula || null;
  return {
    mode: formula ? (revisionFormula ? 'revision' : 'adjustable') : 'fixed',
    formula,
    reference_index_date: editor.referenceIndexDate || null,
  };
};

const toEngineOption = (option, editor) => ({
  id: String(option.id ?? option.clientId ?? ''),
  title: String(option.title || 'Option'),
  selected: Boolean(option.selected),
  chapters: buildEngineChapters(option.sections || [], editor),
  direct_expenses: option.directExpenses || [],
  overheads: option.overheads || [],
  selling_price_expenses: option.sellingPriceExpenses || [],
  discount: option.discount || null,
});

export const buildQuoteEngineRequest = (editor) => {
  const expenses = expenseGroups(editor || {});
  const reference = editor?.quoteNumber || editor?.title || editor?.quoteVersionId || 'QUOTE-DRAFT';
  return {
    schema_version: QUOTE_ENGINE_SCHEMA_VERSION,
    quote: {
      reference: String(reference),
      currency: assertEuro(editor?.currencyCode),
      context: {
        affair_id: editor?.affairId ? String(editor.affairId) : null,
        project_id: editor?.projectId ? String(editor.projectId) : null,
        partner_id: editor?.companyId ? String(editor.companyId) : null,
        site_label: editor?.siteLabel || editor?.affairAddress || null,
        object: editor?.objectText || editor?.title || null,
        lot: Array.isArray(editor?.lots) ? editor.lots.join(', ') : editor?.lot || null,
        market_type: editor?.marketType || null,
        building_type: editor?.projectType || null,
      },
      chapters: buildEngineChapters(editor?.sections || [], editor || {}),
      direct_expenses: expenses.directExpenses,
      overheads: expenses.overheads,
      imputed_charge_rules: expenses.imputedChargeRules,
      nature_coefficients: natureCoefficients(editor || {}),
      pricing: {
        mode: 'line_based_full_cost',
        coefficient: '1',
        margin_rate: '0',
        markup_rate: '0',
      },
      selling_price_expenses: expenses.sellingPriceExpenses,
      global_discount: globalDiscount(editor || {}),
      financial_terms: financialTerms(editor || {}),
      price_adjustment: priceAdjustment(editor || {}),
      commercial_rounding: { mode: commercialRoundingMode(editor?.roundingMode) },
      options: (editor?.options || []).map((option) => toEngineOption(option, editor || {})),
    },
  };
};

const flattenEngineChapters = (chapters = []) => chapters.flatMap((chapter) => [
  ...(chapter.lines || []),
  ...flattenEngineChapters(chapter.chapters || []),
]);

const costsByNature = (line, nature) => asNumber(line.costs_by_nature?.[nature]);

export const adaptQuoteEngineResponse = (response, editor) => {
  const calculation = response?.calculation || {};
  const engineLines = flattenEngineChapters(calculation.chapters || []);
  const hasForcedPrices = engineLines.some((line) => line.forced_unit_selling_price != null);
  const sourceLines = (editor?.sections || []).flatMap((section) => section.lines || []);
  const sourceById = new Map(sourceLines.map((line) => [String(line.id), line]));

  const lines = engineLines.map((line) => {
    const source = sourceById.get(String(line.line_id)) || {};
    const imputed = asNumber(line.imputed_charge);
    const overhead = asNumber(line.allocated_overhead);
    const automatic = hasForcedPrices && Object.prototype.hasOwnProperty.call(line, 'automatic_selling_price_ht')
      ? (line.automatic_selling_price_ht === null ? null : asNumber(line.automatic_selling_price_ht))
      : asNumber(line.gross_selling_price_ht);
    return {
      ...source,
      id: source.id ?? line.line_id,
      description: source.description || line.label,
      quantity: line.quantity,
      unit: line.unit,
      directCost: asNumber(line.direct_cost),
      totalEstimatedHours: asNumber(line.labor_hours),
      laborCost: asNumber(line.labor_cost),
      materialCost: asNumber(line.material_cost),
      subcontractingTotalCost: costsByNature(line, 'subcontracting'),
      imputedCharge: imputed,
      allocatedOverhead: overhead,
      allocatedExpenseAmount: imputed + overhead,
      costPrice: asNumber(line.cost_price),
      grossSellingPriceHT: asNumber(line.gross_selling_price_ht),
      discountAmount: asNumber(line.discount_amount),
      sellingPriceHT: asNumber(line.net_selling_price_ht),
      vatRate: asNumber(line.tax_rate) * 100,
      vatAmount: asNumber(line.tax_amount),
      sellingPriceTTC: asNumber(line.total_ttc),
      clientTotalPrice: asNumber(line.net_selling_price_ht),
      finalTotalPrice: asNumber(line.net_selling_price_ht),
      totalPrice: asNumber(line.net_selling_price_ht),
      resultAmount: asNumber(line.result),
      totalCalculatedSellingPrice: automatic,
      commercialAdjustmentAmount: hasForcedPrices && Object.prototype.hasOwnProperty.call(line, 'commercial_adjustment_ht')
        ? (line.commercial_adjustment_ht === null ? null : asNumber(line.commercial_adjustment_ht))
        : asNumber(line.net_selling_price_ht) - asNumber(line.gross_selling_price_ht),
      totalEffectiveSellingPrice: asNumber(line.net_selling_price_ht),
    };
  });

  const lineById = new Map(lines.map((line) => [String(line.id), line]));
  // Une ligne locale sans résultat (ex. saisie temporaire) reste visible, mais sans calcul inventé.
  sourceLines.forEach((line) => {
    if (!lineById.has(String(line.id))) lines.push({ ...line, calculationPending: true });
  });

  const totals = {
    totalDirectCost: asNumber(calculation.total_direct_cost),
    totalBaseAmount: asNumber(calculation.total_direct_cost),
    totalIndirectAmount: asNumber(calculation.total_direct_expenses)
      + asNumber(calculation.total_imputed_charges)
      + asNumber(calculation.total_allocated_overhead),
    totalDirectExpenses: asNumber(calculation.total_direct_expenses),
    totalImputedCharges: asNumber(calculation.total_imputed_charges),
    totalAllocatedOverhead: asNumber(calculation.total_allocated_overhead),
    totalCostPrice: asNumber(calculation.total_cost_price),
    totalCalculatedSellingPrice: asNumber(hasForcedPrices ? calculation.total_automatic_selling_price_ht : calculation.total_gross_selling_price_ht),
    totalGrossSellingPriceHT: asNumber(calculation.total_gross_selling_price_ht),
    totalDiscount: asNumber(calculation.total_discount),
    totalCommercialAdjustment: hasForcedPrices ? asNumber(calculation.commercial_adjustment_ht)
      : -asNumber(calculation.total_discount) + asNumber(calculation.rounding_delta),
    totalHT: asNumber(calculation.total_net_ht),
    totalSellingPriceHT: asNumber(calculation.total_net_ht),
    totalTax: asNumber(calculation.total_tax),
    totalVat: asNumber(calculation.total_tax),
    totalTTC: asNumber(calculation.total_ttc),
    totalMargin: asNumber(calculation.quote_result),
    grossMarginAmount: asNumber(calculation.quote_result),
    marginRate: calculation.markup_rate === null || calculation.markup_rate === undefined
      ? 0 : asNumber(calculation.markup_rate) * 100,
    grossMarginRate: calculation.markup_rate === null || calculation.markup_rate === undefined
      ? 0 : asNumber(calculation.markup_rate) * 100,
    totalLaborCost: lines.reduce((sum, line) => sum + asNumber(line.laborCost), 0),
    totalPurchaseCost: lines.reduce((sum, line) => sum + asNumber(line.materialCost), 0),
    totalSubcontractingCost: lines.reduce((sum, line) => sum + asNumber(line.subcontractingTotalCost), 0),
    totalHours: lines.reduce((sum, line) => sum + asNumber(line.totalEstimatedHours), 0),
    roundingDelta: asNumber(calculation.rounding_delta),
  };

  const visibleLines = lines.filter((line) => line.isClientVisible !== false);
  const optionViews = (calculation.options || []).map((optionResult) => {
    const sourceOption = (editor?.options || []).find(
      (option) => String(option.id) === String(optionResult.option_id)
    ) || {};
    const sourceOptionLines = (sourceOption.sections || []).flatMap((section) => section.lines || []);
    const sourceOptionById = new Map(sourceOptionLines.map((line) => [String(line.id), line]));
    const calculatedOptionLines = flattenEngineChapters(optionResult.chapters || []).map((line) => {
      const source = sourceOptionById.get(String(line.line_id)) || {};
      return {
        ...source,
        id: source.id ?? line.line_id,
        description: source.description || line.label,
        quantity: line.quantity,
        unit: line.unit,
        sellingPriceHT: asNumber(line.net_selling_price_ht),
        vatRate: asNumber(line.tax_rate) * 100,
        vatAmount: asNumber(line.tax_amount),
        sellingPriceTTC: asNumber(line.total_ttc),
      };
    }).filter((line) => line.isClientVisible !== false);
    return {
      id: optionResult.option_id,
      title: optionResult.title || sourceOption.title || 'Option',
      selected: Boolean(optionResult.selected),
      taxBreakdown: optionResult.tax_breakdown || [],
      taxCalculationMethod: optionResult.tax_calculation_method || null,
      taxAllocationMethod: optionResult.tax_allocation_method || null,
      lines: calculatedOptionLines,
      totals: {
        costPrice: asNumber(optionResult.total_cost_price),
        totalHT: asNumber(optionResult.total_net_ht),
        totalVat: asNumber(optionResult.total_tax),
        totalTTC: asNumber(optionResult.total_ttc),
      },
    };
  });
  return {
    schemaVersion: response?.schema_version,
    engineVersion: response?.engine_version || calculation.engine_version || null,
    taxBreakdown: calculation.tax_breakdown || [],
    taxCalculationMethod: calculation.tax_calculation_method || null,
    taxAllocationMethod: calculation.tax_allocation_method || null,
    calculation,
    minute: response?.minute || null,
    salesSheet: response?.sales_sheet || null,
    lines,
    internalLines: lines,
    clientLines: visibleLines,
    visibleLines,
    optionViews,
    totals,
    settings: {
      defaultLaborRate: editor?.defaultLaborRate ?? 0,
      defaultVatRate: editor?.defaultVatRate ?? 0,
      defaultMarginCoefficient: editor?.defaultMarginCoefficient ?? 1,
      roundingMode: editor?.roundingMode || 'cent',
    },
    warnings: { messages: [] },
  };
};

export const emptyQuoteEngineCalculation = (editor) => ({
  schemaVersion: QUOTE_ENGINE_SCHEMA_VERSION,
  engineVersion: null,
  calculation: null,
  minute: null,
  salesSheet: null,
  lines: (editor?.sections || []).flatMap((section) => section.lines || []),
  internalLines: (editor?.sections || []).flatMap((section) => section.lines || []),
  clientLines: (editor?.sections || []).flatMap((section) => section.lines || []).filter((line) => line.isClientVisible !== false),
  visibleLines: (editor?.sections || []).flatMap((section) => section.lines || []).filter((line) => line.isClientVisible !== false),
  optionViews: [],
  totals: {},
  settings: {
    defaultLaborRate: editor?.defaultLaborRate ?? 0,
    defaultVatRate: editor?.defaultVatRate ?? 0,
    defaultMarginCoefficient: editor?.defaultMarginCoefficient ?? 1,
    roundingMode: editor?.roundingMode || 'cent',
  },
  warnings: { messages: [] },
});
